import PasswordField from '@/components/form/PasswordField';
import { CenteredLayout } from '@/components/layouts/CenteredLayout';
import { Form } from '@/components/primitives/Form';
import { resetPasswordLoadingSelector } from '@/features/loading/loadingSelectors';
import { resetPassword } from '@/features/reset-password/resetPasswordActions';
import { usePageTitle } from '@/hooks/usePageTitle';
import usePasswordComplexity from '@/hooks/usePasswordComplexity';
import { useTranslation } from '@/i18n';
import { TransAlert } from '@/i18n/trans/alert';
import { TransText } from '@/i18n/trans/text';
import NewPasswordFormSkeleton from '@/routes/reset-password/NewPasswordFormSkeleton';
import { useDispatch, useSelector } from '@/store/utils';
import { breakpoints } from '@/utils/breakpoints';
import {
  createResetPasswordSchema,
  type ResetPasswordValues,
} from '@/utils/zodSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Card,
  Divider,
  Icons,
  Typography,
} from '@packages/webshop-design-system';
import { type FC, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

interface NewPasswordFormProps {
  passwordResetHash: string;
}

const NewPasswordForm: FC<NewPasswordFormProps> = ({ passwordResetHash }) => {
  const loading = useSelector(resetPasswordLoadingSelector);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });
  const { passwordComplexity, loading: passwordRequirementsLoading } =
    usePasswordComplexity();
  const { t } = useTranslation();

  usePageTitle('resetPassword');

  const form = useForm<ResetPasswordValues>({
    resolver: zodResolver(
      createResetPasswordSchema(t, passwordComplexity?.regex)
    ),
    defaultValues: {
      password: '',
      repeatPassword: '',
    },
  });
  const {
    trigger,
    formState: { submitCount },
  } = form;

  const onSubmit = useCallback(
    async ({ password }: ResetPasswordValues) => {
      try {
        await dispatch(resetPassword({ password, passwordResetHash })).unwrap();

        toast.success(<TransAlert i18nKey="passwordSuccessfullyChanged" />);
        navigate('/login');
      } catch {
        toast.error(<TransAlert i18nKey="passwordChangeFailed" />);
      }
    },
    [dispatch, navigate, passwordResetHash]
  );

  const validatePasswordFields = useCallback(
    () => submitCount > 0 && trigger(['password', 'repeatPassword']),
    [submitCount, trigger]
  );

  return (
    <CenteredLayout>
      <div className="mx-auto flex w-full max-w-[410px] flex-col gap-6 p-4 tablet:my-auto laptop:p-0">
        <Typography
          variant={isLaptopOrBigger ? 'title' : 'mobile-title'}
          className="pb-2"
        >
          <TransText i18nKey="resetYourPassword" />
        </Typography>

        {passwordRequirementsLoading ? (
          <NewPasswordFormSkeleton />
        ) : (
          <Card className="flex flex-col gap-4 rounded-xl">
            <Typography variant={isLaptopOrBigger ? 'heading2' : 'subtitle'}>
              <TransText i18nKey="createNewPassword" />
            </Typography>
            {passwordComplexity?.regexLocalization?.description && (
              <Typography variant="body1">
                {passwordComplexity.regexLocalization.description}
              </Typography>
            )}
            <Divider />
            <Form
              form={form}
              onSubmit={form.handleSubmit(onSubmit)}
              className="flex flex-col gap-3"
            >
              <PasswordField
                label={<TransText i18nKey="password" />}
                name="password"
                onBlur={validatePasswordFields}
                required
              />
              <PasswordField
                label={<TransText i18nKey="repeatNewPassword" />}
                name="repeatPassword"
                onBlur={validatePasswordFields}
                required
              />
              <Button
                data-testid="confirm-password-reset-button"
                type="submit"
                size="medium"
                startAdornment={<Icons.check />}
                isPending={loading}
              >
                <TransText i18nKey="confirm" />
              </Button>
            </Form>
          </Card>
        )}
      </div>
    </CenteredLayout>
  );
};

export default NewPasswordForm;
